import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import {
  HTML_BLOCK_FALLBACK_HEIGHT,
  HTML_BLOCK_IFRAME_SANDBOX,
  clampReportedHeight,
} from 'src/editor/content-creation/helpers/block-types/html';

// Messages we accept from the embedded page. Anything else is ignored.
const HEIGHT_MESSAGE_TYPE = 'journey:html-block:height';

type Props = {
  url: string;
  title?: string;
  // Set in an editor surface, where the frame must not swallow clicks meant for
  // the surrounding UI. A recipient wants the opposite: the artifact is the
  // point, so it stays interactive in the player.
  interactionDisabled?: boolean;
  className?: string;
  // Set on a surface whose height the artifact cannot change.
  //
  // A step in the step editor lives inside .journey-experience-node, which is
  // height: 100% of the viewport — every other content type there (PDF, video,
  // image) is designed to fit one screen. An HTML artifact is the first type
  // that legitimately wants to be taller, and growing to 2439px inside a
  // ~900px parent made the whole preview pane scroll.
  //
  // Here the frame fills its parent and the artifact scrolls inside itself,
  // which is what a recipient expects of a step: one screen, scroll the
  // content. In the block editor a block CAN grow, so this stays off there and
  // the reported height is used.
  fillParent?: boolean;
};

/**
 * Renders a user-supplied HTML artifact in a sandboxed cross-origin iframe.
 *
 * Extracted from the block editor's HtmlBlock (JOU-40) so the step editor and
 * the player can render the same artifact under the same sandbox. The sandbox
 * string and the height clamp are imported rather than restated: they are a
 * security boundary, and a second copy would be a second thing to get wrong.
 *
 * Unlike HtmlBlock this does not persist the reported height. A step has no
 * per-block content to write it back to, and a height that resets on reload is
 * a cosmetic cost, not a correctness one.
 */
export const HtmlArtifactFrame = ({
  url,
  title,
  interactionDisabled = false,
  className,
  fillParent = false,
}: Props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [reportedHeight, setReportedHeight] = useState<number | null>(null);
  // The frame paints white before the artifact renders, which reads as a flash.
  // Fading in on load turns that into one smooth appearance.
  const [loaded, setLoaded] = useState(false);

  const effectiveHeight = reportedHeight ?? HTML_BLOCK_FALLBACK_HEIGHT;

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Identity is established by event.source, not by event.origin.
      //
      // The sandbox omits allow-same-origin, so the frame runs on an opaque
      // origin and every message it sends arrives with event.origin === "null".
      // Comparing that against the serving origin rejected every height report,
      // which is why blocks stayed at the fallback height on QA.
      //
      // event.source is the stronger check anyway: it is a reference to this
      // frame's own contentWindow, so a message from any other frame or window
      // — same origin or not — fails it. Origin could not distinguish two
      // artifacts on one Journey; source can.
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
        return;
      }

      const data = event.data;
      if (!data || data.type !== HEIGHT_MESSAGE_TYPE) {
        return;
      }

      const next = clampReportedHeight(data.height);
      if (next === null) {
        return;
      }

      setReportedHeight((current) => (current === next ? current : next));
    },
    []
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  if (!url) {
    return null;
  }

  return (
    <div
      className={classNames('w-full relative', fillParent && 'h-full', className)}
      // When the parent sets the height, do not fight it with a reported one.
      style={fillParent ? undefined : { height: effectiveHeight }}
      data-testid='html-artifact-frame'
    >
      <iframe
        ref={iframeRef}
        src={url}
        title={title || 'Embedded HTML'}
        onLoad={() => setLoaded(true)}
        className={classNames('w-full h-full rounded-lg border-0 transition-opacity duration-300', {
          'pointer-events-none': interactionDisabled,
          'opacity-0': !loaded,
          'opacity-100': loaded,
        })}
        sandbox={HTML_BLOCK_IFRAME_SANDBOX}
        // Belt and braces alongside the sandbox: keep the artifact away from
        // device APIs it has no reason to reach.
        allow=''
        referrerPolicy='no-referrer'
        loading='lazy'
      />
      {/* With the scrollbar hidden there is no cue that content continues below
          the fold, and a row sliced in half reads as broken rather than
          scrollable. A short fade at the bottom edge restores that cue.
          Only on fillParent: when the frame grows to the reported height there
          is nothing below the fold to hint at. */}
      {fillParent && (
        <div
          aria-hidden
          className='pointer-events-none absolute inset-x-0 bottom-0 h-10 rounded-b-lg
                     bg-gradient-to-b from-transparent to-white/90'
        />
      )}
    </div>
  );
};

export default HtmlArtifactFrame;
